import type { ComponentResponse } from './api/types';
import { isDone, TASK_TYPE } from './blocks/todo';
import { useI18n } from './i18n/context';
import { intlTagFor } from './i18n/locale';

interface TaskProgress {
  total: number;
  completed: number;
}

/** Count the actual task tree, including nested tasks, from the current server document. */
function summarizeTasks(components: readonly ComponentResponse[]): TaskProgress {
  return components.reduce<TaskProgress>((progress, component) => {
    const children = summarizeTasks(component.children);
    const task = component.type === TASK_TYPE;
    return {
      total: progress.total + children.total + Number(task),
      completed: progress.completed + children.completed + Number(task && isDone(component)),
    };
  }, { total: 0, completed: 0 });
}

/** A shared progress summary for editors and readers, refreshed with the authoritative plan. */
export function PlanProgress({ components }: { components: readonly ComponentResponse[] }) {
  const { messages: t, locale } = useI18n();
  const g = t.grove;
  const { total, completed } = summarizeTasks(components);
  if (total === 0) return null;
  const percent = new Intl.NumberFormat(intlTagFor(locale), {
    style: 'percent', maximumFractionDigits: 0,
  }).format(completed / total);

  return (
    <section className="plan-progress card" aria-label={g.progressTitle}>
      <div className="progress-copy">
        <div>
          <span className="progress-label">{g.progressTitle}</span>
          <p>{g.progressSummary(completed, total)}</p>
        </div>
        <strong className="progress-value">{percent}</strong>
      </div>
      <progress className="progress-track" aria-label={g.completedTasks} value={completed} max={total} />
      <p className="progress-caption">{g.remainingTasks(total - completed)}</p>
    </section>
  );
}
